import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { friendlyErrorMessage } from './ErrorMessage';
import UserAutocomplete, { UserStatusIndicator } from './UserAutocomplete';
import { BTN, BTN_XS } from '../constants/buttons';
import { ROLE_LABELS } from '../constants/statuses';
import { useAuth } from '../contexts/AuthContext';
import { useDepartment } from '../contexts/DepartmentContext';
import { useLatestRequest } from '../hooks/useSearchRequest';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import SearchInput from './ui/SearchInput';
import Pagination, { getStoredPageSize } from './Pagination';
import { computeTotalPages, clampPage } from '../utils/clampPage';
import RemoveUserFromProductionModal from './RemoveUserFromProductionModal';
import GlobalOffboardModal from './GlobalOffboardModal';
import InfoTip from './InfoTip';
import ConfirmDialog from './ConfirmDialog';
import OrderActionsMenu from './OrderActionsMenu';
import { collidingIds, withIdSuffix } from '../utils/duplicateLabels';
import DepartmentInvitesPanel from './DepartmentInvitesPanel';
import AddPersonForm, { DepartmentOptions } from './AddPersonForm';
import AddPersonPanel from './AddPersonPanel';
import { emailInUseHandoffMessage, isEmailInUse } from '../constants/emailInUse';
import { FIELD_LABEL_CLASS, FORM_INPUT_FIELD, ROW_EDIT_CONTROL } from '../constants/inputs';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';
import { LAST_MANAGER_REASON, lastManagerBlocksRole } from '../constants/lastManager';
import { ROW_HOVER } from '../constants/rowStyles';
import { formsDiffer, ROW_OPEN_FADE_MS, ROW_CLOSE_FADE_MS } from '../hooks/useExpandRowEditor';
import useMeasuredWidth from '../hooks/useMeasuredWidth';
import { useIsMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import {
  RowEditBar, RowEditPrompt, editableRowClass, stopRowToggleOnControl, ROW_CELL_PAD, ROW_CELL_PAD_EDIT,
} from './budget/ExpandRowParts';
import { InlineEditPanelRow } from './TableRowExpansion';

// Department Access Rights options, in the order this panel has always listed them.
const PEOPLE_ROLE_OPTIONS = [
  ['user', 'User'],
  ['sub_department_lead', 'Sub Dept Lead'],
  ['supervisor', 'Supervisor'],
  ['buyer', 'Buyer'],
];

/**
 * The admin-only "Production" column: which distinct productions is this person
 * attached to, via their department assignments?
 *
 * Deduplicated on `production_id`, NEVER on `production_name`. It used to be
 * `new Set(departments.map(d => d.production_name))`, so a person in two
 * DIFFERENT productions that happen to share a name showed as belonging to one
 * — silently under-reporting an admin's view of that person's reach, on the one
 * screen whose job is to show it. `production_id` is on the very same rows:
 * `RemoveUserFromProductionModal.js` dedupes this exact array by it.
 *
 * Where two productions really do share a name the id is appended to those
 * entries only, so "Deep Water, Deep Water" does not read as a rendering bug.
 * No warning, no reconciliation prompt — duplicate names are legitimate and the
 * UI stays indifferent to them (product owner, 2026-08-05).
 */
export function productionSummary(departments) {
  const byId = new Map();
  (departments || []).forEach(d => {
    if (d.production_id === null || d.production_id === undefined) return;
    if (!d.production_name) return;
    if (!byId.has(d.production_id)) byId.set(d.production_id, d.production_name);
  });
  const entries = Array.from(byId.entries()).map(([id, name]) => ({ id, name }));
  const colliding = collidingIds(entries.map(e => ({ id: e.id, key: e.name })));
  return entries
    .map(e => (colliding.has(e.id) ? withIdSuffix(e.name, e.id) : e.name))
    .join(', ');
}

// Additive "Access Rights" capabilities — orthogonal to the base role (a user
// keeps their role; these stack on top), edited via PUT /api/users/:id.
// 🔀 Stores is PER SEAT since 2026-09-23 (owner rulings 1 + 20): GET/PUT
// /api/users/:id read and write the flag on the person's seat in the department
// YOU are standing in, so the toggle shows and sets that department only.
const CAPABILITY_OPTIONS = [
  {
    key: 'can_manage_stores',
    label: 'Stores',
    description: 'Manage deliveries in THIS department (the one you are standing in) — see the goods-in queue, tick items received, and upload delivery paperwork. Switch department to grant it elsewhere.',
  },
];

// The tooltip on every control withheld because the viewer does not manage the
// person from the department they are standing in (row expand, row status select).
const NOT_MANAGED_HERE_TITLE = 'To manage this person, switch to a department they belong to where you have settings rights';

// Lifecycle sequence: placeholder → invited → active → deactivated
const STATUS_ORDER = ['placeholder', 'invited', 'active', 'deactivated'];

const STATUS_BADGES = {
  placeholder: { label: 'Placeholder', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300' },
  invited: { label: 'Invited', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  active: { label: 'Active', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
  deactivated: { label: 'Deactivated', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
};

// What each status MEANS, on the whole pill (owner 2026-11-03: the placeholder
// pill "doesnt have a tool tip explaining that they havent been invited").
export const STATUS_TIPS = {
  placeholder: 'Not invited yet. They are on your list but cannot sign in until you send an invite (Actions → Invite).',
  invited: 'Invite sent. Waiting for them to set a password and sign in.',
  active: 'Signed up and can sign in.',
  deactivated: 'Paused. They cannot sign in until reactivated.',
};

/**
 * Deactivated rows are DE-EMPHASISED, not hidden and not disabled.
 *
 * Product owner, 2026-08-06: "deactivated should show too but greyed out".
 * `GET /api/users` stopped filtering by status on the same day, so archived
 * colleagues now arrive in the default list and this is what tells them apart
 * at a glance.
 *
 * Three rules this encodes, each of which someone will otherwise undo:
 *
 *  1. IT IS PURELY VISUAL. The row still expands, its Actions menu still
 *     works, and every action behaves exactly as it does for an active person.
 *     Greying a row and quietly making it unclickable would be a behaviour
 *     change nobody asked for — reactivating somebody is done from this row.
 *  2. IT MUST STAY READABLE. `text-gray-500` on white is ~4.8:1 and
 *     `dark:text-gray-400` on `gray-800` is ~6.4:1, both comfortably legible.
 *     Do not reach for `text-gray-400` in light mode (~2.6:1) or an `opacity-*`
 *     utility — that is dimming, not de-emphasis, and it fails the brief.
 *  3. IT IS NOT A SECOND STATUS LANGUAGE. `UserStatusIndicator` and the
 *     `STATUS_BADGES` pill already say WHAT the status is; this only says the
 *     row is quieter than its neighbours. Never encode status meaning here.
 *
 * Both halves carry a `dark:` pair, as every colour class in this repo must.
 */
/**
 * A control in the Departments table: the plain form box, but sized to its
 * WIDEST option (`w-auto`), so a long sub-department name is never cut off
 * ("— No sub-de…" was the owner's screenshot). Phone keeps the full-width box.
 */
const DEPT_TABLE_SELECT = `${FORM_INPUT_FIELD.replace('w-full ', '')} w-auto max-w-full`;
/**
 * The users table's `<col>` widths (md+, `table-fixed`): fixed so the open
 * row's inputs cannot resize a column. A col whose cells are hidden at a
 * width is `hidden` there too; Department(s) takes the remainder.
 */
// Phone is wide enough for "+44 7700 900000" in its input at 1280 (measured).
const COLUMN_WIDTHS = [
  'md:w-[28%] lg:w-[18%]', 'md:w-[16%] lg:w-[12%]', 'md:w-[34%] lg:w-[23%]', 'md:w-[22%] lg:w-[15%]',
  'hidden lg:table-column lg:w-[14%]', 'hidden lg:table-column',
];
const ADMIN_COLUMN_WIDTHS = [
  'md:w-[28%] lg:w-[18%] xl:w-[14%]', 'md:w-[16%] lg:w-[12%] xl:w-[11%]', 'md:w-[34%] lg:w-[23%] xl:w-[23%]',
  'md:w-[22%] lg:w-[15%] xl:w-[15%]', 'hidden lg:table-column lg:w-[14%] xl:w-[11%]', 'hidden lg:table-column',
  'hidden xl:table-column xl:w-[10%]',
];
const PANEL_HEADING_CLASS ='mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200';
const EMAIL_LOCKED_HINT = 'Only an admin can change the email of an account that can sign in.';

const DEACTIVATED_NAME_CLASS ='text-gray-500 dark:text-gray-400';
const ACTIVE_NAME_CLASS = 'text-gray-800 dark:text-gray-200';

// Plain-language summary of each role, surfaced in tooltips.
const ROLES_SUMMARY_TITLE =
  'User — creates & views their own orders only.\n' +
  'Sub Dept Lead — first approver for their sub-department.\n' +
  'Supervisor — senior oversight & final sign-off in the approval chain.\n' +
  'Buyer — runs the full order lifecycle for the department (approve, order, track delivery).';

const ROLES_SUMMARY = (
  <span>
    <b>User</b> — creates &amp; views their own orders only.<br />
    <b>Sub Dept Lead</b> — first approver for their sub-department.<br />
    <b>Supervisor</b> — senior oversight &amp; final sign-off.<br />
    <b>Buyer</b> — full order lifecycle for the department.
  </span>
);

// 🔻 DELETED 2026-09-24 (LR, owner rulings (c)+(d)): `LEGACY_ROLE_LABEL`,
// `LEGACY_ROLE_TIP_TITLE`, `LEGACY_ROLE_TIP` and the Add-person "Legacy account role"
// select that wrote the unscoped `user_roles` row through
// PUT /api/roles/user-assignments. The unscoped buyer tag is removed from everyone,
// and site admin is granted ONLY by a database statement run for the owner
// (docs/RUNBOOK.md) — there is no in-app control for it. Pre-deletion SHA d2ec4431.

/**
 * The TABLE role column (2026-08-17, owner ruling). It shows each person's role
 * in the ONE department the viewer is currently standing in — the sealed-box
 * active-department role the server sends as `activeDepartmentRole`, never a
 * union across departments. Owner: "it should just say the role she has in THIS
 * department not all departments."
 *
 * This REPLACED the "Legacy account role" column, which rendered the unscoped
 * `user_roles` row (`person.role`) and was correctly blank for a proper
 * department buyer. The Add-person form's matching field is DELETED too
 * (2026-09-24, see the note above).
 *
 * A site admin is not standing in a single department, so `activeDepartmentRole`
 * is null for them. SINCE 2026-09-03 (owner ruling, "only for site admin") the
 * server sends an admin viewer `departmentRoles` instead — the person's role in
 * EVERY department they hold — and the cell renders that list, abbreviated when
 * several distinct roles exist (owner: "if multiple roles exist them trancate
 * like buy,sup, sub dep etc"). The cell branches on the PAYLOAD field, never on
 * the viewer's identity, so the server's `req.isGlobalAdmin` gate is the single
 * source of truth and a non-admin can never render another department's role.
 *
 * Abbreviation is a RULE, not a role→string map: `role_definitions` is a table
 * and custom roles exist, so a lookup table would render a custom role blank or
 * raw inconsistently. See abbreviateRoleName below.
 */
const ACTIVE_ROLE_LABEL = 'Department Access Rights';

const ACTIVE_ROLE_TIP_TITLE =
  'This person’s role in the department you are currently viewing (chosen in '
  + 'the department switcher). Authority is per department, so it can differ in '
  + 'their other departments. A site admin, who is not standing in a single '
  + 'department, sees each person’s roles across every department they hold, '
  + 'abbreviated when there are several — hover a row for the full list.';

const ACTIVE_ROLE_TIP = (
  <span>
    This person&rsquo;s role in the department <b>you are currently viewing</b>
    {' '}(chosen in the department switcher).<br /><br />
    Authority is per department, so it can differ in their other departments.
    A site admin, who is not standing in a single department, sees each
    person&rsquo;s roles across <b>every department they hold</b>, abbreviated
    when there are several &mdash; hover a row for the full list.
  </span>
);

/**
 * Abbreviate a role display name BY RULE (never a lookup table — custom roles
 * exist): first 3 letters of each of the first two words. "Buyer" → "Buy",
 * "Supervisor" → "Sup", "Sub Department Lead" → "Sub Dep" — the owner's own
 * examples ("buy, sup, sub dep"). A custom "Workshop Coordinator" → "Wor Coo",
 * so every role, seeded or custom, truncates the same way.
 */
function abbreviateRoleName(displayName) {
  return String(displayName).trim().split(/[\s_-]+/)
    .slice(0, 2)
    .map(word => word.slice(0, 3))
    .join(' ');
}

/**
 * The Department Access Rights cell + its sort key, from ONE function so the
 * rendered text and the comparator cannot drift.
 *
 * Returns null when the payload carries no `departmentRoles` (every non-admin
 * viewer — the server omits the key entirely): callers keep the sealed-box
 * `activeDepartmentRole` path exactly as before. For an admin payload:
 *
 * ⚠️ KNOWN LIMIT (recorded 2026-09-03, review finding — unreachable today):
 * dedup runs on the FULL display name BEFORE abbreviation, so two DISTINCT
 * roles that abbreviate alike ("Supervisor" / "Superintendent" → "Sup") would
 * render "Sup, Sup". Today `department_assignments.role`'s CHECK admits only
 * the four seeded names, none of which collide; if custom roles ever widen
 * that CHECK, revisit — the `title` already disambiguates in the meantime.
 * Deduping AFTER abbreviation instead would silently merge two real roles
 * into one token, which hides information rather than duplicating it.
 *  - ONE distinct role → the full display name, unabbreviated.
 *  - Several → each abbreviated (rule above), deduplicated in order of first
 *    appearance, at most 3 shown then "+N" — the cell stays one short token
 *    list however many departments someone holds, and the `title` (full
 *    "Department: Role" list) carries the untruncated detail. jsdom cannot see
 *    layout, so how the truncation LOOKS is a browser claim, not a tested one.
 */
function accessRightsInfo(person) {
  if (!Array.isArray(person.departmentRoles)) return null;
  const names = person.departmentRoles.map(r => r.role_display_name).filter(Boolean);
  const uniq = [...new Set(names)];
  const title = person.departmentRoles.map(r =>
    `${r.department_name}${r.sub_department_name ? ` - ${r.sub_department_name}` : ''}: ${r.role_display_name}`
  ).join('; ');
  let text = null;
  if (uniq.length === 1) {
    text = uniq[0];
  } else if (uniq.length > 1) {
    const shown = uniq.slice(0, 3).map(abbreviateRoleName);
    const overflow = uniq.length - 3;
    text = shown.join(', ') + (overflow > 0 ? ` +${overflow}` : '');
  }
  return { text, title, sortKey: uniq.join(', ').toLowerCase() };
}

/**
 * What to say when the invitation was RAISED but its email could not be sent.
 *
 * 🔑 ONE definition. There were TWO invite call sites on this page until
 * 2026-10-19, when the status dropdown (and its "set status to invited AND
 * send the invite" branch) was deleted by owner ruling; `handleInvite` (the
 * Actions menu's "Invite" / "Re-invite") is the one left.
 *
 * The wording names the recovery route deliberately: an `invited` user can sign
 * in through Forgot Password, which mints a reset token for exactly that
 * status. Without that sentence the operator knows something is wrong and not
 * what to do about it.
 */
const inviteEmailFailedMessage = (email) =>
  `Invited ${email}, but the email could not be sent. `
  + `Ask them to sign in with "Forgot password" instead.`;

/**
 * The Error to throw for a refused person write. A `409 email_in_use` — setting
 * a placeholder's email to an address another account holds — becomes THE
 * placeholder hand-off (owner, 2026-09-23: *"IF they are already invited the
 * only option is to delete the placeholder"*; constants/emailInUse.js), naming
 * the holder only when the server did. Anything else keeps the server's words.
 * Used by every email writer here: Save (PUT /api/users/:id) and both
 * `POST /api/users/:id/invite` sites.
 */
function handoffError(data, fallback) {
  if (isEmailInUse(data)) return new Error(emailInUseHandoffMessage(data));
  return new Error((data && data.error) || fallback);
}

// `ADD_PERSON_EMAIL_TAKEN` moved to `AddPersonForm.js` with the form (2026-09-24).

/**
 * The person panel's Actions-menu tips (owner ruling 2026-10-19): every item
 * carries one. The invite tip and Impersonate's are new wording; the other five
 * reuse the `title` their buttons carried before the menu.
 */
export const sendInviteTip = (name) => `Email ${name} a link to set a password and sign in.`;
export const impersonateTip = (name) =>
  `See CrewForge exactly as ${name} sees it. Everything you do is recorded against your own name.`;
export const PAUSE_TIP =
  'Pause (reversible): stop login and remove from pickers, but keep everything — approval chains, teams, equipment, history. Use for leave / sabbatical.';
export const reactivateTip = (to) =>
  `Return them to ${STATUS_BADGES[to]?.label || to}, the status they had before they were paused. Department seats removed by an offboard are not restored — re-add those by hand.`;
export const DEACTIVATE_GLOBALLY_TIP =
  "Nuclear: deactivates the user globally across every production. Use 'Remove from production' for normal departures.";
export const DELETE_PERSON_TIP =
  'Permanently remove — no audit trail. Only for test accounts or for users added by mistake.';

/**
 * The Actions menu's "Site admin" group (owner ruling B, 2026-10-19: *"add a
 * small site admin section in the dropdown"*). Its items are the server's
 * `status_transitions` (GET /api/users/:id — the lifecycle table filtered
 * through the PATCH /:id/status rule, never a client copy) MINUS the moves
 * another item in this menu already makes: → deactivated is Pause…,
 * deactivated → the server's `restore_status` is Reactivate… (R1, 2026-10-21;
 * it was always → placeholder), placeholder → invited is Invite.
 */
export const siteAdminStatusTargets = (fromStatus, serverTransitions, restoreStatus = null) =>
  (Array.isArray(serverTransitions) ? serverTransitions : []).filter((to) => !(
    to === 'deactivated'
    || (fromStatus === 'deactivated' && restoreStatus != null && to === restoreStatus)
    || (fromStatus === 'placeholder' && to === 'invited')
  ));

/**
 * The status Reactivate… returns a paused person to (owner ruling R1,
 * 2026-10-21: *"They go back to whatever they were before being paused"*), or
 * null when it is not offered. THE SERVER DECIDES: `restore_status` on the
 * DETAIL payload names the target, and it counts only when the same payload's
 * `status_transitions` admits it for THIS caller — so a manager with no
 * provable history, and anyone on their own row (R3, `[]`), get no Reactivate.
 * Never derived here from the person's own fields.
 */
export const reactivateTarget = (fromStatus, detail) => {
  if (fromStatus !== 'deactivated' || !detail) return null;
  const to = detail.restore_status;
  return to && Array.isArray(detail.status_transitions) && detail.status_transitions.includes(to) ? to : null;
};

export const siteAdminStatusTip = (name, to) => ({
  active: `Set ${name} to Active straight away, without an invitation. No email is sent.`,
  invited: `Set ${name} back to Invited. No email is sent — use Invite to send one.`,
  placeholder: `Set ${name} back to Placeholder: a named slot, not someone who signs in. No email is sent.`,
}[to] || `Set ${name} to ${to}. No email is sent.`);

// Save and Cancel carry a native `title` (zero layout cost — an ⓘ here once
// widened the table's scrollable region on hover, 2026-09-24).
export const SAVE_CHANGES_TIP = "Save your changes to this person's role, team and settings.";
export const CANCEL_EDIT_TIP = 'Discard your changes and close this panel without saving.';

export default function PeopleManager() {
  const { user: authUser, impersonate } = useAuth();
  const { activeProduction, activeDepartment, assignments, capabilities, capabilitiesFor } = useDepartment();
  const isGlobalAdmin = authUser?.isAdmin;
  // May the viewer ADMINISTER this person? The SAME question the server's four
  // person routes ask (helpers/userAccess.js#callerManagesUserInActiveDepartment,
  // 2026-09-23): canManageDepartmentSettings in the department the viewer is
  // STANDING IN, and the person is in THAT department — never another shared
  // department, never the unscoped `isBuyer`. "Use the department switches."
  // The list spans every department the viewer sits in, so without this a
  // manager of A standing in B could open the edit panel on a person, and every
  // control in it would 403.
  // 🕶️ The server also counts a crew-ONLY relation, which the list payload does
  // not carry; such a person is not expandable here (a hidden control, never an
  // offered one that 403s). A My Space is refused server-side; its capability
  // set is empty here. Pinned by PeopleManager.manageableRows.test.js.
  const activeDeptId = Number(activeDepartment?.department_id);
  const canManagePerson = (person) => !!isGlobalAdmin
    || (capabilities?.canManageDepartmentSettings === true
      && (person.departments || []).some((d) => Number(d.department_id) === activeDeptId));
  // ── Pending department invites + "Add & send invite" (2026-09-23, P6) ──
  // Both are about the department the viewer is STANDING IN, never a My Space.
  // ONE gate for the list, Withdraw and Add & send invite — ruling 24, only
  // managers invite: `canAssignDepartmentRoles` here, the question
  // `POST /api/invitations` asks. The list and withdraw routes move onto the
  // same capability in P3's fix round (coordinator, 2026-09-23); until then a
  // 403 on the list simply hides the section. A site admin holds no
  // assignment, so the `isGlobalAdmin` disjunct carries them. Never `isBuyer`.
  const inviteDeptId = activeDepartment && !activeDepartment.production_is_personal
    && Number.isInteger(activeDeptId) && activeDeptId > 0 ? activeDeptId : null;
  const canInviteByEmail = !!isGlobalAdmin || capabilities?.canAssignDepartmentRoles === true;
  const canListInvites = canInviteByEmail;
  // ── The ONE "+ Add person" form (owner ruling 2026-09-24; AddPersonForm.js). ──
  // Each action is offered only to a caller its server route accepts:
  //   "Add & send invite" — POST /api/invitations: site admin, or
  //     canAssignDepartmentRoles in the department (the gate above), and a
  //     real department to invite INTO;
  //   "Add, invite later" — POST /api/users: site admin, or
  //     canManageDepartmentSettings in the ACTIVE department (and the seat
  //     write, POST/PUT /api/departments/:id/members, asks the same).
  // Never `isBuyer`.
  const canSendInvite = canInviteByEmail && (!!inviteDeptId || !!isGlobalAdmin);
  const canAddLater = !!isGlobalAdmin || capabilities?.canManageDepartmentSettings === true;
  const [showAddPerson, setShowAddPerson] = useState(false);
  // Impersonate asks through the house ConfirmDialog, never `window.confirm`
  // (POL2 A6, owner 2026-09-25 — he impersonates demo personas in front of a
  // client, and the browser's grey box looked wrong). `{ id, display_name }`.
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);
  // The "+ Add person" RESULT — `{ message, tone }` or null. ⚖️ Owner ruling
  // (2026-09-25): it stays until the × is clicked, the form is re-opened, a
  // department switch (below), or this section unmounts — "already uses
  // CrewForge, asked to join" vanished in the `success` banner's 3 seconds
  // before it could be read. Deliberately NOT the `success` state: every
  // other success keeps its 3s timeout.
  const [addPersonResult, setAddPersonResult] = useState(null);
  // A department SWITCH closes a half-filled form: it was FOR the old department.
  // It also clears the Add person result, which NAMES that department ("asked
  // to join SFX") and would otherwise outlive the switch — this component is
  // not remounted by one (review F1, 2026-09-25).
  // Only a switch — not the first resolution of the active department after
  // load (null -> id), which used to close a form opened in the first second
  // (measured in the browser, 2026-09-24), nor a transient null mid-refetch.
  const lastInviteDeptRef = useRef(inviteDeptId);
  useEffect(() => {
    if (inviteDeptId == null) return; // a transient null (a refetch) is not a switch
    const prev = lastInviteDeptRef.current;
    lastInviteDeptRef.current = inviteDeptId;
    if (prev != null && prev !== inviteDeptId) {
      setShowAddPerson(false);
      setAddPersonResult(null);
    }
  }, [inviteDeptId]);
  // Departments where the caller is a buyer — used to gate inline sub-department editing.
  const buyerDeptIds = new Set((assignments || []).filter(a => a.role === 'buyer').map(a => a.department_id));
  const canManageDeptSubDept = (deptId) => isGlobalAdmin || buyerDeptIds.has(deptId);
  // May the viewer CHANGE this person's role in the seat's department? The
  // server's question (POL2 B, 2026-09-25): `PUT /api/departments/:id/members/
  // :userId` runs `mayGrantDepartmentRole` on a role change, which requires
  // `canAssignDepartmentRoles` IN THAT DEPARTMENT (LR2) — not the settings
  // capability that opens this panel, and not the ACTIVE department. The
  // defect this closes: the panel offered a select for EVERY seat the person
  // holds, so a viewer with no seat, or a lesser seat, in THAT seat's
  // department got a dropdown the server 403s. (A "settings but not assign"
  // role is NOT reachable today: `department_assignments.role` admits only
  // the four system roles on both engines and no API writes either
  // capability column — the fixture uses it only because it discriminates.)
  // 🔻 The server's SUBSET rule (a caller may not grant a role carrying a
  // capability they lack) is NOT modelled. It depends on DATA: on the dev DB
  // (2026-09-25) supervisor lacks `can_manage_budget`, so a supervisor
  // offering "Buyer" would 403; production READ 1 when measured on
  // 2026-08-06 (root CLAUDE.md, Schema & migrations) — re-SELECT before relying.
  const canChangeSeatRole = (deptId) => !!isGlobalAdmin
    || capabilitiesFor?.(deptId)?.canAssignDepartmentRoles === true;
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  // A THIRD banner state: the action SUCCEEDED but something about it needs
  // acting on. Today's only producer is an invitation whose email could not be
  // sent — the user IS invited and the DB work stands, so a red error would be
  // a lie, and a green "Invitation sent" would be the original defect.
  // ⚠️ Unlike `success` it does NOT auto-dismiss (see the effect below): it
  // carries an INSTRUCTION the operator has to act on, and three seconds is
  // not long enough to read one.
  const [warning, setWarning] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hideTest, setHideTest] = useState(() => localStorage.getItem('sfx_users_hide_test') === 'true');
  const [showDemo, setShowDemo] = useState(() => localStorage.getItem('sfx_users_show_demo') === 'true');

  const [saving, setSaving] = useState(false);

  // Expandable row
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [editForm, setEditForm] = useState({});
  // The form AS OPENED (null while the detail loads, and after a save lands):
  // the dirty test compares against it — `formsDiffer`, the shared rule.
  const [editOriginal, setEditOriginal] = useState(null);
  // A click held by the three-way prompt: `{ what, proceed }`.
  const [pending, setPending] = useState(null);
  const generationRef = useRef(0);
  const detailRequestRef = useRef(0);
  // Below `md` the row keeps today's stacked panel + sticky Save bar (plan
  // constraint 5); from `md` up the row's own cells are the inputs.
  const isMobile = useIsMobileOrderViewport();
  const [tableWrapRef, tableWrapWidth] = useMeasuredWidth();
  const [allDepartments, setAllDepartments] = useState([]);
  // Sub-departments available per department, keyed by department_id (loaded on expand).
  const [subDeptsByDept, setSubDeptsByDept] = useState({});
  const [addDeptId, setAddDeptId] = useState('');
  const [addDeptRole, setAddDeptRole] = useState('user');
  const [addingToDept, setAddingToDept] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [closingId, setClosingId] = useState(null);

  // Sort state
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Pagination — see `pagePeople` below. A new query returns to page 1.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(getStoredPageSize);
  useEffect(() => { setCurrentPage(1); }, [statusFilter, searchQuery, hideTest, showDemo]);

  // Stale-response guard for the search box below. `searchQuery` is a
  // dependency of `fetchPeople`, so every COMMITTED search term starts a
  // request; the box debounces at 300ms, so a burst of keystrokes is one
  // request rather than one each — but a pause past the debounce still puts
  // two in flight. See the block comment on `fetchPeople`.
  const beginRequest = useLatestRequest();

  /**
   * 🔑 EVERY setState BELOW IS GATED ON `isCurrent()`, INCLUDING THE `catch` AND
   * THE `finally`. This is a server search — `searchQuery` is a dependency, and
   * the effect below re-runs whenever that identity changes — so more than one
   * request is genuinely in flight while somebody types a name with any pause
   * in it. Responses are not ordered.
   *
   * THE DEFECT THIS CLOSES (product owner, 2026-08-10: *"if I type a name in
   * fast the search returns more than it should"*). A shorter prefix matches a
   * SUPERSET — `%s%` versus `%steve%`, server/routes/users.js:230-233 — so when
   * the abandoned prefix's answer lands last it shows MORE people than the box
   * asks for and inflates the "All (N)" pill. That is what made this surface's
   * symptom "too many" rather than merely "wrong", and it is why it was the one
   * he noticed. Pinned by `PeopleManager.staleResponse.test.js`.
   *
   * 🚨 THE BOX IS DEBOUNCED **AND** GUARDED SINCE 2026-08-10, AND THE GUARD IS
   * NOT THE DEBOUNCE'S JUNIOR PARTNER — IT IS THE ONLY THING CLOSING THE RACE.
   * The two landed as separate changes for exactly that reason: the guard came
   * first, alone, as the correctness fix, and the debounce followed as a
   * request-VOLUME change. `hooks/useSearchRequest.js` states the rule and
   * `CLAUDE.md` states it in capitals — A DEBOUNCE IS NOT A STALE-RESPONSE
   * GUARD. Debouncing narrows the window in which two requests overlap; it
   * never closes it.
   *
   * ⚠️ SO DO NOT READ "it debounces now" AS "the race is handled". Type a
   * prefix, pause past 300ms, type more, and two requests are in flight with
   * the narrower one likely to answer first — the original defect, unchanged
   * in kind and merely harder to stumble into. That is not an argument, it is
   * driven to failure in `PeopleManager.staleResponse.test.js`, which removes
   * the guard's protection and watches the wrong rows arrive.
   *
   * WHAT THE DEBOUNCE DID BUY: typing "steve" was five requests and is now
   * one, which is the whole of its contribution and is pinned by
   * `PeopleManager.searchRequestCount.test.js`. It buys nothing else, and in
   * particular it buys no correctness.
   */
  const fetchPeople = useCallback(async (silent = false) => {
    const isCurrent = beginRequest();
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams();
      // A status pill NARROWS; no pill means "everyone in the department I am
      // standing in" (owner 2026-11-02, ruling A — the server scopes to the
      // active department, so a department switch must refetch: see the effect).
      //
      // There is deliberately no opt-in parameter here any more. `GET
      // /api/users` returns every status by default as of 2026-08-06, so the
      // "All" view is simply the unparameterised call. This used to send
      // `?includeDeactivated=true`, a flag the server gated on
      // `req.isGlobalAdmin` and therefore SILENTLY IGNORED for a department
      // buyer or supervisor — which is how a department holding 21
      // `placeholder` crew showed them an empty list. The flag is gone from the
      // server; do not re-add it here, and do not add a replacement. See the
      // status block in server/routes/users.js and docs/USER_STATUS.md.
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      if (searchQuery) params.set('search', searchQuery);
      // Server now excludes test users by default; admins opt in via includeTest.
      if (!hideTest) params.set('includeTest', 'true');
      // Demo users (Project Aurora) are hidden by default; admins opt in.
      if (showDemo) params.set('includeDemo', 'true');
      const res = await apiGet(`/api/users?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch people');
      const data = await res.json();
      if (!isCurrent()) return;
      setPeople(data);
    } catch (err) {
      // A stale REJECTION is as damaging as a stale success — it paints an
      // error banner over a list that loaded perfectly well.
      if (!isCurrent()) return;
      setError(friendlyErrorMessage(err));
    } finally {
      // A stale `finally` clears the spinner while the real request is still
      // open, so the list reads as settled when it is not.
      if (isCurrent()) setLoading(false);
    }
  }, [statusFilter, searchQuery, hideTest, showDemo, beginRequest]);

  // activeDeptId: the server scopes the list to the active department, so a switch refetches.
  useEffect(() => { fetchPeople(); }, [fetchPeople, activeDeptId]);

  // Fetch all departments once so the expanded row can offer a picker
  useEffect(() => {
    apiGet('/api/departments').then(r => r.ok ? r.json() : []).then(data => {
      setAllDepartments(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(''), 3000); return () => clearTimeout(t); }
  }, [success]);
  // 🔴 There is deliberately NO auto-dismiss effect for `warning`. Do not add
  // one "for consistency" with `success` — the green banner reports something
  // that needs no response, this one tells the operator to go and ask a person
  // to use Forgot Password.

  // Bring an expanded person's panel into view (demo polish, 2026-09-25): a
  // row near the bottom used to expand entirely off-screen. It fires when the
  // DETAIL lands — the panel's final geometry, since only its opacity animates
  // — and once per expansion, so a later detail refresh (a role change, a
  // save) never scrolls the page out from under the user. `nearest` does
  // nothing when the panel is already visible; the panel row's scroll margins
  // keep its summary row above it and clear the phone bottom nav below it.
  const expandedPanelRef = useRef(null);
  const revealedIdRef = useRef(null);
  useEffect(() => {
    if (!expandedId) { revealedIdRef.current = null; return; }
    if (!expandedDetail || revealedIdRef.current === expandedId) return;
    revealedIdRef.current = expandedId;
    const el = expandedPanelRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [expandedId, expandedDetail]);

  /**
   * THE ROW EDITOR'S STATE — the PROPERTIES of `hooks/useExpandRowEditor.js`,
   * kept here rather than the hook itself (2026-11-03): the hook's `open(row)`
   * seeds the form AND its baseline synchronously from the row
   * (`formFrom(row)`), while this row's form comes from `GET /api/users/:id`
   * (Stores is on the detail only) and the hook has no way to re-baseline once
   * it lands. What is the same, and must stay the same:
   *   - SINGLE-EXPAND; the 50ms open / 250ms close fade (the hook's constants);
   *   - an open bumps a generation, so a pending collapse timer cannot wipe
   *     the row just opened, and a late detail for a row no longer open is
   *     dropped (it would seed the wrong person's form): EVERY detail writer
   *     checks `detailRequestRef`, the open AND the three seat-write refetches;
   *   - a dirty row ASKS (Keep editing · Discard changes · Save and close)
   *     before it collapses or another row opens; the click is HELD and re-run
   *     on Discard, or after a save that LANDED. A failed save runs nothing.
   * Dirty covers what Save sends (Name, Email, Phone, Stores). Access rights
   * and sub-department still save the moment they change, as they always have.
   */
  const isDirty = expandedId != null && closingId !== expandedId
    && editOriginal != null && formsDiffer(editForm, editOriginal);

  // Animated collapse — no prompt (Cancel, and the actions that end an edit).
  const collapseUser = () => {
    if (!expandedId) return;
    const closing = expandedId;
    const generation = generationRef.current;
    setClosingId(closing);
    setTimeout(() => {
      if (generationRef.current !== generation) {
        setClosingId((cur) => (cur === closing ? null : cur));
        return;
      }
      setExpandedId(null);
      setExpandedDetail(null);
      setEditOriginal(null);
      setClosingId(null);
    }, ROW_CLOSE_FADE_MS);
  };

  // Expand/collapse a row — a dirty row asks first (see above).
  const toggleExpandRef = useRef(null);
  const toggleExpand = (personId, { force = false } = {}) => {
    const name = people.find((p) => p.id === personId)?.display_name || 'this person';
    const hold = (what) => setPending({ what, proceed: () => toggleExpandRef.current(personId, { force: true }) });
    if (expandedId === personId) {
      if (isDirty && !force) { hold(`close ${name}`); return; }
      collapseUser();
      return;
    }
    if (expandedId != null && isDirty && !force) { hold(`open ${name}`); return; }
    openUser(personId);
  };
  toggleExpandRef.current = toggleExpand;

  // Open a row — fetch full details on expand
  const openUser = async (personId) => {
    generationRef.current += 1;
    const request = ++detailRequestRef.current;
    setOpeningId(personId);
    setExpandedId(personId);
    setClosingId(null);
    setExpandedDetail(null);
    setEditOriginal(null);
    setTimeout(() => setOpeningId((cur) => (cur === personId ? null : cur)), ROW_OPEN_FADE_MS);
    try {
      const res = await apiGet(`/api/users/${personId}`);
      if (!res.ok) throw new Error('Failed to fetch user details');
      const data = await res.json();
      if (detailRequestRef.current !== request) return;
      setExpandedDetail(data);
      const seeded = {
        display_name: data.display_name || '',
        email: data.email || '',
        phone: data.phone || '',
        can_manage_stores: !!data.can_manage_stores,
      };
      setEditForm(seeded);
      setEditOriginal(seeded);
      // Load sub-departments for each shared department so the inline picker can render options.
      const sharedDepts = (data.departments || []).filter(d => !d.department_is_personal && !d.production_is_personal);
      const map = {};
      await Promise.all(sharedDepts.map(async (d) => {
        try {
          const r = await apiGet(`/api/sub-departments?department_id=${d.department_id}`);
          if (r.ok) map[d.department_id] = await r.json();
        } catch { /* non-fatal */ }
      }));
      if (detailRequestRef.current !== request) return;
      setSubDeptsByDept(map);
    } catch (err) {
      if (detailRequestRef.current !== request) return;
      setError(friendlyErrorMessage(err));
    }
  };

  /**
   * Change a member's role IN ONE DEPARTMENT. It writes exactly one row:
   * `department_assignments`, for that department. It must never touch the
   * unscoped `user_roles` row, and that is a property of the SYSTEM, not a
   * preference of this handler — see docs/ROLES.md §F.2 (c).
   *
   * WHAT THIS USED TO DO, AND WHY IT WAS WRONG. After the department write it
   * also `PUT /api/roles/user-assignments/:userId` with the `role_definitions`
   * row whose NAME matched the department role, "keeping Account Access in sync
   * with Department Access Rights". `user_roles.user_id` is UNIQUE, so there is
   * exactly ONE such row per person and it is not per department. Setting
   * somebody supervisor in ONE department therefore minted an unscoped
   * SUPERVISOR row — the leaks-across-departments role the product owner has
   * ruled must not exist ("No Global roles apart from site admin is a critical
   * mention", docs/ROLES.md §A / ruling 1). No attack needed: an ordinary
   * dropdown, used the ordinary way.
   *
   * IT WAS ALSO A SILENT REVOCATION, WHICH IS THE HALF NOBODY HAD NOTICED.
   * Because that row is unique it was OVERWRITTEN, not added to. `departmentScope`
   * reads it for `req.isBuyer` (:177-182), and `requireAdminOrBuyer`
   * (server/helpers/roles.js:61-67) is `req.isGlobalAdmin || req.isBuyer` and
   * nothing else, at 10 live call sites — 8 in routes/approvals.js, 2 in
   * routes/users.js. So setting anyone to `supervisor`, `sub_department_lead` or
   * `user` in ANY ONE department stripped their legacy `buyer` row and took
   * approval-workflow and template management off them EVERYWHERE, including in
   * departments the edit was not about. Every self-signed-up user is exposed to
   * that: the wizard writes the legacy `buyer` row for everyone whatever seat
   * they chose (docs/ROLES.md §A.2, §F.2 (c)).
   *
   * WHY NOTHING REPLACES IT, MEASURED RATHER THAN ASSUMED. The unscoped row's
   * only remaining live effect is `req.isBuyer`. `req.canViewBudget` /
   * `req.canManageBudget` were also SET from it, but nothing under `server/`
   * read either one after tranche 3 increment 2 deleted every consumer, and both
   * flags were themselves REMOVED on 2026-09-08 together with the
   * `role_definitions … WHERE name IN (…)` read that fed them (grep
   * `req.isBuyer =` in `middleware/departmentScope.js` — neither flag is written
   * anywhere in that file any more). `req.isSupervisor` and `req.isSubDeptLead` come from
   * `department_assignments`, never from `user_roles`. So an unscoped
   * `supervisor` row granted precisely NOTHING while still destroying whatever
   * row it replaced. Writing the legacy `buyer` row instead — for every
   * department role, as the wizard does at signup — was considered and rejected:
   * it would UPGRADE everyone touched by this dropdown to an unscoped buyer,
   * which is strictly worse than the defect.
   *
   * NOTHING IS REVOKED BY THIS CHANGE. An existing `user_roles` row is left
   * exactly as it is, so every current holder keeps all 10 routes. What stops is
   * a FUTURE grant: a person whose unscoped row is `user` and who is promoted to
   * department `buyer` here no longer picks `req.isBuyer` up as a side effect.
   * Everything resolved per department — budget, breakdown, department settings,
   * role assignment, order management, RA authoring — is unaffected, because all
   * of it goes through `departmentAuthority` and the `department_assignments`
   * row this handler still writes. The residue is those 10 `requireAdminOrBuyer`
   * routes, and the fix for those is to migrate them to
   * `canManageDepartmentSettings` in tranche 3, NOT to mint an unscoped role.
   *
   * 🔻 `selectedRoleId` and `handleSaveEdit`'s re-persist of it are DELETED
   * (2026-09-24, LR): no client surface writes `user_roles` any more.
   */
  const handleSetMemberDeptRole = async (deptId, userId, newDeptRole) => {
    if (!userId) return;
    const request = detailRequestRef.current;
    try {
      const res = await apiPut(`/api/departments/${deptId}/members/${userId}`, { role: newDeptRole });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error); }
      const detailRes = await apiGet(`/api/users/${userId}`);
      if (detailRes.ok) {
        const detail = await detailRes.json();
        if (detailRequestRef.current !== request) return;
        setExpandedDetail(detail);
      }
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Assign/clear a member's sub-department within a department (admin or that dept's buyer).
  const handleSetMemberSubDept = async (deptId, userId, rawVal) => {
    if (!userId) return;
    const sub_department_id = rawVal ? parseInt(rawVal, 10) : null;
    const request = detailRequestRef.current;
    try {
      const res = await apiPut(`/api/departments/${deptId}/members/${userId}`, { sub_department_id });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to update sub-department'); }
      const detailRes = await apiGet(`/api/users/${userId}`);
      if (detailRes.ok) {
        const detail = await detailRes.json();
        if (detailRequestRef.current !== request) return;
        setExpandedDetail(detail);
      }
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // 🔻 `handleAddPerson`, its submit guard and its Idempotency-Key MOVED into
  // `AddPersonForm.js` ("Add, invite later") on 2026-09-24, unchanged in
  // substance: POST /api/users with a per-attempt key, reused on a retry.

  // THE save: PUT /api/users/:id with the form, body unchanged. Resolves true
  // only on a `res.ok` write — the prompt's "Save and close" runs its held
  // click on true alone, so a refused save leaves the row open with the edit.
  const saveEdit = async (id) => {
    try {
      setSaving(true);
      setError(null);
      // Save user details
      const res = await apiPut(`/api/users/${id}`, editForm);
      const data = await res.json();
      // The placeholder email hand-off (constants/emailInUse.js) — see
      // `handoffError` below.
      if (!res.ok) throw handoffError(data, 'Failed to update');
      setSuccess(`"${data.display_name}" updated`);
      setEditOriginal(null);
      fetchPeople(true);
      return true;
    } catch (err) {
      setError(friendlyErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };
  const handleSaveEdit = async (id) => {
    if (await saveEdit(id)) collapseUser();
  };
  // The form an ACTION reads when it runs (Invite reads the email): a held
  // click runs after the render it was made in, so it reads this ref, never a
  // closure. Discard puts it back to the saved form before the click runs.
  const editFormRef = useRef(editForm);
  editFormRef.current = editForm;
  // The three-way prompt's answers (RowEditPrompt reads this editor shape).
  const rowPrompt = {
    pending,
    keepEditing: () => setPending(null),
    discardAndContinue: () => {
      const held = pending;
      setPending(null);
      if (editOriginal) {
        editFormRef.current = editOriginal;
        setEditForm(editOriginal);
      }
      if (held) held.proceed();
    },
    saveAndContinue: async () => {
      const held = pending;
      setPending(null); // closes FIRST, as the hook's does
      if (expandedId == null) return;
      if (await saveEdit(expandedId) !== true) return;
      if (held) held.proceed();
    },
  };

  // Invite / Re-invite (the Actions menu)
  // ── Double-submit guard + Idempotency-Key (2026-08-13). `inviting` was a
  // state flag set before the await — the fast-double-click hole; a doubled
  // invite is two invitation emails and two token mints. The key is minted on
  // arm, reused on a retry of a failed attempt, cleared on success — the
  // server treats the header as optional.
  const { guard: guardInvite } = useSubmitGuard();
  const inviteIdem = useIdempotencyKey();
  const [inviting, setInviting] = useState(false);
  const handleInvite = (person) => {
    // If no email in editForm or on person, prompt
    const email = editFormRef.current.email || person.email;
    if (!email) {
      setError('Please enter an email address before sending an invite');
      return;
    }
    guardInvite(async () => {
    try {
      setInviting(true);
      setError(null);
      setWarning('');
      const res = await apiPost(`/api/users/${person.id}/invite`, { email }, {
        headers: { 'Idempotency-Key': inviteIdem.mint() },
      });
      const data = await res.json();
      if (!res.ok) throw handoffError(data, 'Failed to send invite');
      inviteIdem.clear();
      // The route answers 200 whether or not the email went out — the user IS
      // invited either way. `email_warning` is present ONLY on failure, so its
      // presence is the test; never match on the message text.
      if (data.email_warning) {
        setWarning(inviteEmailFailedMessage(email));
      } else {
        setSuccess(`Invitation sent to ${email}`);
      }
      setExpandedId(null);
      setExpandedDetail(null);
      setEditOriginal(null);
      fetchPeople();
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setInviting(false);
    }
    });
  };

  // Two separate offboard targets — see RemoveUserFromProductionModal
  // (everyone, per-production) and GlobalOffboardModal (site admin only).
  const [removeFromProductionTarget, setRemoveFromProductionTarget] = useState(null); // { id, display_name }
  const [globalOffboardTarget, setGlobalOffboardTarget] = useState(null); // { id, display_name }

  // Reactivate confirm — surfaces the limit of the action: PATCH /:id/status
  // only flips users.status. Department assignments wiped by a previous cascade
  // offboard are NOT restored automatically. Admin needs to know this before
  // clicking, since the audit log entry's productions_at_offboard field is the
  // manual restore reference.
  const [reactivateConfirm, setReactivateConfirm] = useState(null); // { person, to } — `to` from the server

  // Delete user
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { user, linkedData, confirmName }
  const handleDeleteClick = async (person) => {
    try {
      const res = await apiGet(`/api/users/${person.id}?check=true`);
      if (!res.ok) throw new Error('Failed to check user data');
      // Try DELETE with ?check=true for linked data check
      const checkRes = await apiDelete(`/api/users/${person.id}?check=true`);
      const data = await checkRes.json();
      setDeleteConfirm({ user: person, linkedData: data.linkedData, confirmName: '' });
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm || deleteConfirm.confirmName !== deleteConfirm.user.display_name) return;
    try {
      setError(null);
      const res = await apiDelete(`/api/users/${deleteConfirm.user.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete user');
      setSuccess(`"${deleteConfirm.user.display_name}" permanently deleted`);
      setDeleteConfirm(null);
      collapseUser();
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Deactivate asks through the house ConfirmDialog (review E1, 2026-10-08),
  // same wording as the window.confirm it replaced. A 409 last_department_manager
  // (the last-manager floor) arrives as the server's sentence in the error banner.
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const handleDeactivate = (person) => setDeactivateTarget(person);
  const confirmDeactivate = async () => {
    const person = deactivateTarget;
    setDeactivateTarget(null);
    if (!person) return;
    try {
      setError(null);
      const res = await apiPatch(`/api/users/${person.id}/status`, { status: 'deactivated' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to deactivate');
      setSuccess(`"${person.display_name}" deactivated`);
      collapseUser();
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Site admin status change (the Actions menu's Site admin group) — asks
  // through ConfirmDialog, then PATCH /:id/status, which records the change in
  // user_lifecycle_log.
  const [statusChangeTarget, setStatusChangeTarget] = useState(null); // { person, to }
  const confirmStatusChange = async () => {
    const target = statusChangeTarget;
    setStatusChangeTarget(null);
    if (!target) return;
    const { person, to } = target;
    try {
      setError(null);
      const res = await apiPatch(`/api/users/${person.id}/status`, { status: to });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change status');
      setSuccess(`"${person.display_name}" is now ${STATUS_BADGES[to]?.label || to}`);
      collapseUser();
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // `to` is the server's restore_status (see `reactivateTarget`), never chosen here.
  const handleReactivate = async (person, to) => {
    try {
      setError(null);
      const res = await apiPatch(`/api/users/${person.id}/status`, { status: to });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reactivate');
      setSuccess(`"${person.display_name}" reactivated — now ${STATUS_BADGES[to]?.label || to}`);
      collapseUser();
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    }
  };

  // Assign the currently-expanded user to a department inline. Uses the same
  // POST /api/departments/:id/members endpoint as AdminSettings' + Add Member
  // flow, always supplying user_id (never username).
  const handleAddToDept = async (person) => {
    if (!addDeptId || !person?.id) return;
    const request = detailRequestRef.current;
    try {
      setError(null);
      setAddingToDept(true);
      const res = await apiPost(`/api/departments/${addDeptId}/members`, {
        user_id: person.id,
        role: addDeptRole,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add to department');
      // BUG (staging, 2026-08-06): a new department did not appear in the
      // production dropdown until the page was refreshed. The seat was written
      // correctly; the client simply never refetched. DepartmentContext listens
      // for this event and re-reads /api/roles/my-assignments (:82-86).
      //
      // Success path ONLY, after the server has committed — a refetch that races
      // the write returns the old list and reproduces the bug it is meant to fix.
      //
      // ⚠️ Nothing happens for a SITE ADMIN adding themselves, and that is
      // correct, not a bug to "fix": an admin needs no membership row, so
      // my-assignments has nothing new to return.
      window.dispatchEvent(new Event('sfx-role-change'));
      // Refresh the expanded detail so the new assignment badge appears
      const detailRes = await apiGet(`/api/users/${person.id}`);
      if (detailRes.ok) {
        const detail = await detailRes.json();
        if (detailRequestRef.current !== request) return;
        setExpandedDetail(detail);
      }
      const deptName = allDepartments.find(d => String(d.id) === String(addDeptId))?.name || 'department';
      setAddDeptId('');
      setAddDeptRole('user');
      setSuccess(`"${person.display_name}" added to ${deptName}`);
      fetchPeople(true);
    } catch (err) {
      setError(friendlyErrorMessage(err));
    } finally {
      setAddingToDept(false);
    }
  };

  const statusCounts = people.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const toggleHideTest = () => {
    setHideTest(prev => {
      const next = !prev;
      localStorage.setItem('sfx_users_hide_test', String(next));
      return next;
    });
  };

  const toggleShowDemo = () => {
    setShowDemo(prev => {
      const next = !prev;
      localStorage.setItem('sfx_users_show_demo', String(next));
      return next;
    });
  };

  /**
   * The expanded person panel's Actions ▾ (owner ruling 2026-10-19). Each item
   * keeps the visibility gate and the confirm its BUTTON had — moved, never
   * re-derived: Invite / Re-invite (no confirm, as before) · Impersonate (site admin,
   * not yourself; ConfirmDialog) · — · Pause… (ConfirmDialog) or Reactivate…
   * (its dialog) · Remove from production… (its modal) · — · Deactivate
   * globally… (site admin; its modal) · Delete… (site admin; its dialog).
   * Every item carries a tooltip. The panel only exists for a person the
   * caller may manage (`canManagePerson` gates the row's expand), which is the
   * gate the deleted status `<select>` carried.
   */
  const personActionItems = (person) => {
    const name = person.display_name;
    const isDeactivated = person.status === 'deactivated';
    const separator = (key) => ({
      key,
      render: () => <div role="separator" className="my-1 border-t border-gray-200 dark:border-gray-700" />,
    });
    // Site admin group (owner ruling B, 2026-10-19) — LAST, site admin only,
    // and only the moves the server reports for this person (see
    // `siteAdminStatusTargets`). Read off the DETAIL payload of THIS person.
    const detailHere = expandedDetail && expandedDetail.id === person.id ? expandedDetail : null;
    // R3 (2026-10-21): no status item on your OWN row — the server refuses it
    // (own_status_change) and reports `status_transitions: []` for it.
    const isOwnRow = person.id === authUser?.id;
    const restoreTo = reactivateTarget(person.status, detailHere);
    // `!isOwnRow` is a belt beside the server's `[]` for one's own account.
    const adminTargets = isGlobalAdmin && detailHere && !isOwnRow
      ? siteAdminStatusTargets(person.status, detailHere.status_transitions, detailHere.restore_status)
      : [];
    const siteAdminGroup = adminTargets.length === 0 ? [] : [
      separator('sep-site-admin'),
      {
        key: 'site-admin-heading',
        render: () => (
          <div data-testid="people-site-admin-heading"
            className="px-3 pt-1 pb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">Site admin</div>
        ),
      },
      ...adminTargets.map((to) => ({
        key: `status-${to}`,
        what: `set ${name} to ${STATUS_BADGES[to]?.label || to}`,
        label: `Set to ${STATUS_BADGES[to]?.label || to}…`,
        onClick: () => setStatusChangeTarget({ person, to }),
        title: siteAdminStatusTip(name, to),
        testId: `people-set-status-${to}`,
      })),
    ];
    const items = [
      {
        key: 'invite',
        what: `invite ${name}`,
        emailOnlyRunsAtOnce: true,
        // Owner 2026-10-19 (keep it): the pre-U6 wording — Re-invite for
        // someone already invited, Invite otherwise. Behaviour unchanged.
        label: inviting ? 'Sending...' : (person.status === 'invited' ? 'Re-invite' : 'Invite'),
        // Green (owner 2026-11-03: "maybe should be green to stand out").
        tone: 'approve',
        onClick: () => handleInvite(person),
        disabled: inviting,
        title: sendInviteTip(name),
        testId: 'people-row-invite',
      },
      isGlobalAdmin && person.id !== authUser?.id && {
        key: 'impersonate',
        what: `impersonate ${name}`,
        label: 'Impersonate',
        tone: 'nav',
        onClick: () => setImpersonateTarget({ id: person.id, display_name: name }),
        title: impersonateTip(name),
        testId: 'people-impersonate',
      },
      // The separator only when something follows it in this group.
      (restoreTo || (!isDeactivated && (!isOwnRow || activeProduction || isGlobalAdmin))) && separator('sep-status'),
      isDeactivated && restoreTo && {
        key: 'reactivate',
        what: `reactivate ${name}`,
        label: 'Reactivate…',
        tone: 'approve',
        onClick: () => setReactivateConfirm({ person, to: restoreTo }),
        title: reactivateTip(restoreTo),
        testId: 'people-reactivate',
      },
      !isDeactivated && !isOwnRow && {
        key: 'pause',
        what: `pause ${name}`,
        label: 'Pause…',
        tone: 'progress',
        onClick: () => handleDeactivate(person),
        title: PAUSE_TIP,
        testId: 'people-pause',
      },
      !isDeactivated && (activeProduction || isGlobalAdmin) && {
        key: 'remove-from-production',
        what: `remove ${name} from the production`,
        label: 'Remove from production…',
        tone: 'reject',
        // Pass departments so the modal can render its production picker for
        // the admin flow (when activeProduction is null). Buyers have an
        // activeProduction set and won't hit the picker branch.
        onClick: () => setRemoveFromProductionTarget({ id: person.id, display_name: name, departments: person.departments }),
        title: activeProduction
          ? `Remove ${name} from every department in ${activeProduction.name}. They keep their account and other assignments.`
          : `Remove ${name} from a chosen production. They keep their account and other assignments.`,
        testId: 'people-remove-from-production',
      },
      isGlobalAdmin && separator('sep-admin'),
      !isDeactivated && isGlobalAdmin && !isOwnRow && {
        key: 'deactivate-globally',
        what: `deactivate ${name} globally`,
        label: 'Deactivate globally…',
        tone: 'reject',
        onClick: () => setGlobalOffboardTarget({ id: person.id, display_name: name }),
        title: DEACTIVATE_GLOBALLY_TIP,
        testId: 'people-deactivate-globally',
      },
      isGlobalAdmin && {
        key: 'delete',
        what: `delete ${name}`,
        label: 'Delete…',
        tone: 'reject',
        onClick: () => handleDeleteClick(person),
        title: DELETE_PERSON_TIP,
        testId: 'people-delete',
      },
      ...siteAdminGroup,
    ];
    // Review F2 (2026-11-03, spec GC1): every one of these ends or re-renders
    // the row (a collapse, a dialog whose success collapses it, a navigation),
    // so on a DIRTY row it is a held click like any other: Keep editing runs
    // nothing, Discard runs it, Save and close saves and then runs it, a
    // failed save runs nothing.
    // 🔑 INVITE WITH ONLY THE EMAIL EDITED RUNS AT ONCE (coordinator ruling B):
    // typing an address and pressing Invite is the owner's flow — the route
    // sets the address, no Save — pinned by PeopleManager.inviteTypedEmail.test.js.
    // Any OTHER unsaved field still asks, so it is never silently lost.
    const emailOnlyDirty = isDirty && editOriginal != null
      && !formsDiffer({ ...editForm, email: '' }, { ...editOriginal, email: '' });
    return items.map((item) => {
      if (!item || typeof item.onClick !== 'function') return item;
      const { what, emailOnlyRunsAtOnce, ...rest } = item;
      return {
        ...rest,
        onClick: () => {
          if (!isDirty || (emailOnlyRunsAtOnce && emailOnlyDirty)) { item.onClick(); return; }
          setPending({ what, proceed: item.onClick });
        },
      };
    });
  };

  // ── The expanded row's parts (2026-11-03, the house in-row model) ─────────
  // Every control below keeps the gate, handler and request body it had in
  // the stacked "Person details" form it replaced.
  const sharedDepartmentsOf = (detail) => ((detail && detail.departments) || []).filter(
    (d) => !d.department_is_personal && !d.production_is_personal
  );
  // SECURITY 2026-07-31 — mirrors canSetUserEmail in server/helpers/userAccess.js.
  // Email is a credential (forgot-password mails a reset token to it), so a
  // non-admin may only set it while the account cannot yet sign in. The server
  // refuses regardless; this is so the field does not look editable and then 403.
  const emailLockedFor = (person) => !isGlobalAdmin
    && person.status !== 'placeholder' && person.status !== 'invited';
  const setField = (key) => (e) => setEditForm((prev) => ({ ...prev, [key]: e.target.value }));
  // THE last-manager line (one, under the Departments table) every locked
  // seat's select points at — the reason is text, not colour.
  const LOCK_NOTE_ID = 'people-dept-role-lock-note';
  const roleSelect = (d, person, { className, testId, ariaLabel }) => (
    <select
      value={d.role}
      onChange={(e) => handleSetMemberDeptRole(d.department_id, person.id, e.target.value)}
      className={className}
      aria-label={ariaLabel}
      aria-describedby={d.last_manager_locked ? LOCK_NOTE_ID : undefined}
      data-testid={testId}
    >
      {PEOPLE_ROLE_OPTIONS.map(([value, label]) => (
        <option key={value} value={value} disabled={lastManagerBlocksRole(d, value)}>{label}</option>
      ))}
    </select>
  );
  const roleControl = (d, person, className) => (canChangeSeatRole(d.department_id)
    ? roleSelect(d, person, {
      className, testId: `people-dept-role-${d.department_id}`, ariaLabel: `Access rights in ${d.department_name}`,
    })
    : (
      <span className="text-gray-600 dark:text-gray-300" data-testid={`people-dept-role-readonly-${d.department_id}`}>
        {ROLE_LABELS[d.role] || d.role}
      </span>
    ));
  const subDeptControl = (d, person, className) => (canManageDeptSubDept(d.department_id) ? (
    <select
      value={d.sub_department_id ? String(d.sub_department_id) : ''}
      onChange={(e) => handleSetMemberSubDept(d.department_id, person.id, e.target.value)}
      className={className}
      aria-label={`Sub-department in ${d.department_name}`}
      data-testid={`people-subdept-${d.department_id}`}
    >
      <option value="">— No sub-dept —</option>
      {(subDeptsByDept[d.department_id] || []).map((sd) => (
        <option key={sd.id} value={sd.id}>{sd.name}</option>
      ))}
    </select>
  ) : (
    <span className="text-gray-600 dark:text-gray-300">{d.sub_department_name || '—'}</span>
  ));
  // Stores is PER SEAT: the detail names the department its flag is about
  // (`stores_department_id`, the one the viewer stands in), so the box sits in
  // THAT department's row. No such row → the box sits under the table, as the
  // old form offered it.
  const capabilityCheckbox = (cap, ariaLabel) => (
    <input
      type="checkbox"
      checked={!!editForm[cap.key]}
      onChange={(e) => setEditForm((prev) => ({ ...prev, [cap.key]: e.target.checked }))}
      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
      aria-label={ariaLabel}
      title={cap.description}
      data-testid={`people-cap-${cap.key}`}
    />
  );
  const ROLE_TIP = (
    <InfoTip title={`Sets this person's role in this department — where they sit in the order approval workflow and the views & tools they get across the app.\n\n${ROLES_SUMMARY_TITLE}`}>
      <span>Sets this person's role in this department — their place in the order approval workflow and the views &amp; tools they get across the app.<br /><br />{ROLES_SUMMARY}</span>
    </InfoTip>
  );
  const SUB_DEPT_TIP = (
    <InfoTip title="Optional team within the department (e.g. Workshop, Floor). Used to group people and route work — it doesn't change their access.">
      Optional team within the department (e.g. <b>Workshop</b>, <b>Floor</b>) for grouping &amp; routing. Doesn't change their access.
    </InfoTip>
  );
  const DEPT_TH = 'text-left px-2 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600 whitespace-nowrap';
  const DEPT_TD = 'px-2 py-1.5 border-b border-gray-100 dark:border-gray-700 align-middle whitespace-nowrap text-gray-800 dark:text-gray-200';

  /**
   * The Departments block: one entry per department the person holds (My
   * Space excluded, as before) — Department · Access rights · Sub-department ·
   * Stores — then the ONE last-manager line, then "Add to another department".
   * `stacked` is the phone form (a card per department, full-width boxes).
   */
  const renderDepartments = (person, detail, { stacked }) => {
    const rows = sharedDepartmentsOf(detail);
    const storesDeptId = Number(detail.stores_department_id);
    const storesHere = (d) => Number.isInteger(storesDeptId) && storesDeptId > 0
      && Number(d.department_id) === storesDeptId;
    const storesRowShown = rows.some(storesHere);
    const locked = rows.filter((d) => d.last_manager_locked);
    const assignedIds = new Set(rows.map((d) => String(d.department_id)));
    const availableDepts = allDepartments.filter((d) => !assignedIds.has(String(d.id)) && !d.is_personal);
    const thisOne = (d) => (Number(d.department_id) === activeDeptId
      ? <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(this one)</span>
      : null);
    return (
      <div className="space-y-2">
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Not in a department yet.</p>
        ) : stacked ? (
          <div className="space-y-3" data-testid="people-dept-cards">
            {rows.map((d) => (
              <div key={d.department_id} data-department-id={d.department_id}
                className="rounded-md border border-gray-200 dark:border-gray-600 p-3 space-y-2">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{d.department_name}{thisOne(d)}</div>
                <div>
                  <span className={`${FIELD_LABEL_CLASS} flex items-center gap-1`}>Access rights {ROLE_TIP}</span>
                  {roleControl(d, person, FORM_INPUT_FIELD)}
                </div>
                <div>
                  <span className={`${FIELD_LABEL_CLASS} flex items-center gap-1`}>Sub-department {SUB_DEPT_TIP}</span>
                  {subDeptControl(d, person, FORM_INPUT_FIELD)}
                </div>
                {storesHere(d) && CAPABILITY_OPTIONS.map((cap) => (
                  <label key={cap.key} className="flex items-center gap-2 min-h-[44px] text-sm text-gray-800 dark:text-gray-200">
                    {capabilityCheckbox(cap, `${cap.label} in ${d.department_name}`)}
                    {cap.label}
                  </label>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <table className="text-sm" data-testid="people-dept-table">
              <thead>
                <tr>
                  <th className={DEPT_TH}>Department</th>
                  <th className={DEPT_TH}><span className="inline-flex items-center gap-1">Access rights {ROLE_TIP}</span></th>
                  <th className={DEPT_TH}><span className="inline-flex items-center gap-1">Sub-department {SUB_DEPT_TIP}</span></th>
                  {CAPABILITY_OPTIONS.map((cap) => (
                    <th key={cap.key} className={DEPT_TH}>
                      <span className="inline-flex items-center gap-1">{cap.label} <InfoTip title={cap.description}>{cap.description}</InfoTip></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.department_id} data-department-id={d.department_id}>
                    <td className={DEPT_TD}>{d.department_name}{thisOne(d)}</td>
                    <td className={DEPT_TD}>{roleControl(d, person, DEPT_TABLE_SELECT)}</td>
                    <td className={DEPT_TD}>{subDeptControl(d, person, DEPT_TABLE_SELECT)}</td>
                    {CAPABILITY_OPTIONS.map((cap) => (
                      <td key={cap.key} className={`${DEPT_TD} text-center`}>
                        {storesHere(d)
                          ? capabilityCheckbox(cap, `${cap.label} in ${d.department_name}`)
                          : <span className="text-gray-400 dark:text-gray-500" aria-label={`${cap.label}: not in this department`}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {locked.length > 0 && (
          <p id={LOCK_NOTE_ID} data-testid="people-last-manager-note" className="text-xs text-gray-500 dark:text-gray-400">
            {rows.length > 1 ? `${locked.map((d) => d.department_name).join(', ')}: ` : ''}{LAST_MANAGER_REASON}
          </p>
        )}
        {!storesRowShown && CAPABILITY_OPTIONS.map((cap) => (
          <label key={cap.key} className="flex items-center gap-2 min-h-[44px] md:min-h-0 text-sm text-gray-800 dark:text-gray-200">
            {capabilityCheckbox(cap, cap.label)}
            {cap.label}
          </label>
        ))}
        {availableDepts.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="people-add-dept">
            <label htmlFor={`people-add-dept-select-${person.id}`} className="text-xs font-medium text-gray-500 dark:text-gray-400">{rows.length > 0 ? 'Add to another department' : 'Add to a department'}</label>
            <select
              id={`people-add-dept-select-${person.id}`}
              value={addDeptId}
              onChange={(e) => setAddDeptId(e.target.value)}
              className={`${FORM_INPUT_FIELD} sm:w-auto sm:max-w-[11rem]`}
              data-testid="people-add-dept-select"
            >
              <option value="">-- Pick a department --</option>
              <DepartmentOptions departments={availableDepts} />
            </select>
            <select
              value={addDeptRole}
              onChange={(e) => setAddDeptRole(e.target.value)}
              className={`${FORM_INPUT_FIELD} sm:w-auto sm:max-w-[8rem]`}
              aria-label="Access Rights in the new department"
              title="Access Rights in the new department"
              data-testid="people-add-dept-role"
            >
              {PEOPLE_ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <button
              onClick={() => handleAddToDept(person)}
              disabled={!addDeptId || addingToDept}
              className={`${BTN_XS.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid="people-add-dept-btn"
            >
              {addingToDept ? '...' : '+ Add'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCrew = (detail) => {
    const crew = detail.crewMemberships || [];
    if (crew.length === 0) return null;
    return (
      <div data-testid="people-crew-memberships">
        <h4 className={PANEL_HEADING_CLASS}>Crew memberships</h4>
        <div className="flex flex-wrap items-center gap-2">
          {crew.map((cm) => (
            <span key={cm.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs">
              {cm.grade_name} — {cm.department_name}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const actionsMenu = (person, compact) => (
    <OrderActionsMenu
      items={personActionItems(person)}
      ariaLabel={`Actions for ${person.display_name}`}
      idPrefix={`people-actions-${person.id}`}
      triggerTestId="people-actions-trigger"
      panelTestId="people-actions-panel"
      compact={compact}
    />
  );

  // Sort helpers
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const SortArrow = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 text-gray-300 dark:text-gray-600">&#8597;</span>;
    return <span className="ml-1">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };
  const sortedPeople = [...people].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    let av, bv;
    switch (sortField) {
      case 'name': av = (a.display_name || '').toLowerCase(); bv = (b.display_name || '').toLowerCase(); break;
      case 'status': av = STATUS_ORDER.indexOf(a.status); bv = STATUS_ORDER.indexOf(b.status); break;
      case 'email': av = (a.email || '').toLowerCase(); bv = (b.email || '').toLowerCase(); break;
      case 'phone': av = (a.phone || ''); bv = (b.phone || ''); break;
      // Same accessRightsInfo the cell renders from, so an admin's rows sort by
      // what the admin actually sees rather than every row comparing '' to ''.
      case 'activeRole': { const key = (p) => { const ar = accessRightsInfo(p); return ar ? ar.sortKey : (p.activeDepartmentRole?.role_display_name || '').toLowerCase(); }; av = key(a); bv = key(b); break; }
      case 'departments': { const fmt = (x) => (x.departments?.map(d => d.sub_department_name ? `${d.department_name} - ${d.sub_department_name}` : d.department_name).join(', ') || '').toLowerCase(); av = fmt(a); bv = fmt(b); break; }
      case 'production': av = ([...new Set(a.departments?.map(d => d.production_name).filter(Boolean))].join(', ') || '').toLowerCase(); bv = ([...new Set(b.departments?.map(d => d.production_name).filter(Boolean))].join(', ') || '').toLowerCase(); break;
      default: av = ''; bv = '';
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  // ONE PAGE IS RENDERED, never the whole list (2026-10-02, owner: searching
  // as site admin on staging was "very slow"). The server, the debounce and the
  // stale guard were all fast; drawing every row was not — measured on a copy
  // inflated with 5,000 `is_test` signups (a site admin sees them by default):
  // 2.0 s to first row and ~30 ms input lag per keystroke, gone with a page.
  // Sort and the status pills still run over the WHOLE fetched list; only the
  // render is sliced. Page clamped per root CLAUDE.md's Pagination rule.
  // Pinned by PeopleManager.pagination.test.js.
  const totalPages = computeTotalPages(sortedPeople.length, pageSize);
  const page = clampPage(currentPage, totalPages);
  const pagePeople = pageSize === 'ALL'
    ? sortedPeople
    : sortedPeople.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-6">
      {error && (
        <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-lg text-sm">
          {success}
        </div>
      )}
      {/*
        The DEGRADED-SUCCESS banner. Amber sits between the red error (nothing
        happened) and the green success (everything happened) because that is
        exactly what it reports: the invitation stands, the email did not go.
        It carries a Dismiss button rather than a timeout — see the note beside
        the `warning` state.
      */}
      {warning && (
        <div
          className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg text-sm flex items-start gap-3"
          data-testid="people-warning"
        >
          <span className="flex-1">{warning}</span>
          <button
            type="button"
            onClick={() => setWarning('')}
            className="shrink-0 underline hover:no-underline"
            data-testid="people-warning-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/*
          THE SHARED BOX (adopted 2026-08-10). It was a raw <input> at `w-64`
          with a hand-derived 34px height and no aria-label; the shared one
          brings `SEARCH_BOX_WIDTH` (18rem from `sm:` up, full width below) and
          `CONTROL_HEIGHT` through `SEARCH_INPUT_FIELD`, so this box stops being
          the one control on this row without the 44px mobile tap target.

          🔑 300ms — the SearchInput default, taken deliberately and not merely
          inherited. Committing here refetches `GET /api/users?search=`, so the
          request is real; the order pages pass `debounceMs={0}` because their
          search filters an array already in memory, which is the opposite case.
          The Breakdown is the right precedent and it takes the default too.

          🚨 The debounce cut five requests to one. It did NOT close the
          stale-response race and must never be read as having done so — see
          the block comment on `fetchPeople`, which is where `useLatestRequest`
          earns its keep.

          `setSearchQuery` is passed bare on purpose: SearchInput's debounce
          effect lists `onChange` in its dependency array, so an unstable
          handler re-arms the timer on every render and the box never commits.
          A `useState` setter is already stable, so no `useCallback` is needed —
          and wrapping it in one would suggest the identity was in doubt.
        */}
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by name or email..."
          ariaLabel="Search people"
          testId="people-search"
        />
        <div className="flex flex-wrap gap-1">
          <button onClick={() => setStatusFilter('')}
            className={`min-h-[44px] md:min-h-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${!statusFilter ? 'bg-blue-600 dark:bg-blue-500/70 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
          >All ({people.length})</button>
          {STATUS_ORDER.map(key => (
            <button key={key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`min-h-[44px] md:min-h-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${statusFilter === key ? 'bg-blue-600 dark:bg-blue-500/70 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
            >{STATUS_BADGES[key].label} ({statusCounts[key] || 0})</button>
          ))}
        </div>
        <button onClick={toggleHideTest}
          className={`min-h-[44px] md:min-h-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            hideTest
              ? 'bg-amber-500 dark:bg-amber-500/70 text-white'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >Hide Test</button>
        {isGlobalAdmin && (
          <button onClick={toggleShowDemo}
            title="Show Project Aurora demo users (for impersonation during sales demos)"
            className={`min-h-[44px] md:min-h-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              showDemo
                ? 'bg-purple-600 dark:bg-purple-500/70 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >Show Demo</button>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {(canSendInvite || canAddLater) && (
            <button type="button" onClick={() => {
              // Opening the form starts a new add: the previous result goes.
              if (!showAddPerson) setAddPersonResult(null);
              setShowAddPerson((v) => !v);
            }}
              aria-expanded={showAddPerson}
              className={BTN.primary} data-testid="people-add-person">
              {/* The label no longer flips to 'Cancel': the form has its own Cancel,
                  and two Cancels on one screen was a browser-pass finding. */}
              + Add person
            </button>
          )}
        </div>
      </div>

      <DepartmentInvitesPanel
        departmentId={inviteDeptId}
        canList={canListInvites}
        refreshKey={invitesRefreshKey}
      />

      {/* The one "+ Add person" box (2026-11-03, AddPersonPanel.js) for the
          department the viewer stands in — the same box as a department card.
          A site admin stands in none, so they keep the form with its picker. */}
      {showAddPerson && (inviteDeptId && !isGlobalAdmin ? (
        <AddPersonPanel
          dept={{
            id: inviteDeptId,
            name: activeDepartment.department_name,
            production_id: activeDepartment.production_id,
            production_name: activeDepartment.production_name,
          }}
          isGlobalAdmin={!!isGlobalAdmin}
          activeDepartment={activeDepartment}
          canAddExisting={canAddLater}
          canSendInvite={canSendInvite}
          canAddLater={canAddLater}
          onClose={() => setShowAddPerson(false)}
          onDone={(message, tone) => {
            // The box stays open for the next person (owner 2026-11-03).
            setAddPersonResult({ message, tone: tone === 'warning' ? 'warning' : 'success' });
            setInvitesRefreshKey((k) => k + 1);
            fetchPeople(true);
          }}
        />
      ) : (
        <AddPersonForm
          isGlobalAdmin={!!isGlobalAdmin}
          activeDepartment={activeDepartment}
          allDepartments={allDepartments}
          canSendInvite={canSendInvite}
          canAddLater={canAddLater}
          onClose={() => setShowAddPerson(false)}
          onDone={(message, tone) => {
            setAddPersonResult({ message, tone: tone === 'warning' ? 'warning' : 'success' });
            setInvitesRefreshKey((k) => k + 1);
            fetchPeople(true);
          }}
        />
      ))}

      {/* The Add person RESULT, in the slot the form just left — sticky by
          owner ruling (see `addPersonResult`). Green for done, amber for
          "done, but act on this" (the same pair as the banners above). */}
      {addPersonResult && (
        <div
          role="status"
          data-testid="people-add-result"
          data-tone={addPersonResult.tone}
          className={`mb-4 p-3 rounded-lg text-sm border flex items-start gap-3 ${addPersonResult.tone === 'warning'
            ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800'
            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'}`}
        >
          <span className="flex-1">{addPersonResult.message}</span>
          <button
            type="button"
            onClick={() => setAddPersonResult(null)}
            aria-label="Dismiss"
            title="Dismiss"
            data-testid="people-add-result-dismiss"
            className="shrink-0 -my-1 -mr-1 inline-flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-[28px] md:min-w-[28px] rounded-md text-lg leading-none opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {/* Users table */}
      {loading ? (
        <LoadingSpinner />
      ) : people.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm py-8 text-center">No people found.</p>
      ) : (
        // `md:` only (POL2 A2): below md an overflow box would disarm the
        // expanded panel's sticky Save bar; measured, the table does not
        // overflow horizontally at 360/390/393 (its extra columns are md+).
        // The ref measures the scrollport the in-row panel is pinned to.
        <div ref={tableWrapRef} className="md:overflow-x-auto">
          {/* `md:table-fixed` + the `<colgroup>` is what stops a column
              changing width when a row's cells turn into inputs (the Manage
              Budgets rule). A column hidden at this width has a `hidden` col. */}
          <table className="w-full text-sm md:table-fixed">
            <colgroup>
              {(isGlobalAdmin ? ADMIN_COLUMN_WIDTHS : COLUMN_WIDTHS).map((cls, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <col key={i} className={cls} />
              ))}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {[
                  { key: 'name', label: 'Name', hide: '' },
                  { key: 'status', label: 'Status', hide: '' },
                  { key: 'email', label: 'Email', hide: 'hidden md:table-cell' },
                  { key: 'phone', label: 'Phone', hide: 'hidden md:table-cell' },
                  // The person's role IN THE ACTIVE DEPARTMENT (sealed box), sent
                  // by the server as `activeDepartmentRole`. Replaced the
                  // "Legacy account role" column (the unscoped user_roles row).
                  // See ACTIVE_ROLE_LABEL above.
                  { key: 'activeRole', label: ACTIVE_ROLE_LABEL, hide: 'hidden lg:table-cell', activeRoleTip: true },
                  // NOTE (owner, 2026-08-17): this Department(s) list is "almost a
                  // leak but we will allow it for now" — tightening it to the
                  // active department only is a future OWNER decision, not done here.
                  { key: 'departments', label: 'Department(s)', hide: 'hidden lg:table-cell' },
                  { key: 'production', label: 'Production', hide: 'hidden xl:table-cell', adminOnly: true },
                ].filter(col => !col.adminOnly || isGlobalAdmin).map(col => (
                  <th key={col.key}
                    className={`text-left py-2 px-3 font-semibold text-gray-600 dark:text-gray-400 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 ${col.hide}`}
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {col.activeRoleTip && <> <InfoTip title={ACTIVE_ROLE_TIP_TITLE}>{ACTIVE_ROLE_TIP}</InfoTip></>}
                    <SortArrow field={col.key} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagePeople.map(person => {
                const manageable = canManagePerson(person);
                const isExpanded = expandedId === person.id;
                const isClosing = closingId === person.id;
                const badge = STATUS_BADGES[person.status] || STATUS_BADGES.placeholder;
                // Visual de-emphasis only — see DEACTIVATED_NAME_CLASS above.
                // Nothing below branches on this except a colour.
                const isDeactivated = person.status === 'deactivated';
                // 🔀 2026-11-03: the open row's OWN cells are the inputs (md+),
                // once the detail has landed; a closing row is text at once.
                const editing = !isMobile && isExpanded && !isClosing && !!expandedDetail;
                const pad = editing ? ROW_CELL_PAD_EDIT : ROW_CELL_PAD;
                const dimmed = isDirty && expandedId !== person.id;
                const emailLocked = emailLockedFor(person);
                const activeSeat = editing
                  ? sharedDepartmentsOf(expandedDetail).find((d) => Number(d.department_id) === activeDeptId)
                  : null;
                const textInput = (key, type, label, placeholder) => (
                  <div onClick={stopRowToggleOnControl}>
                    <input type={type} value={editForm[key] ?? ''} onChange={setField(key)}
                      aria-label={label} placeholder={placeholder} className={ROW_EDIT_CONTROL} />
                  </div>
                );
                const fadeClass = isClosing || openingId === person.id ? 'opacity-0' : 'opacity-100';

                return (
                  <React.Fragment key={person.id}>
                    {/* Summary row — click to expand */}
                    <tr
                      data-status={person.status}
                      data-testid={`people-row-${person.id}`}
                      className={`border-b border-gray-100 dark:border-gray-700 ${editing ? '' : 'select-none'} ${manageable
                        ? editableRowClass({ expanded: isExpanded, dimmed })
                        : `transition-colors ${ROW_HOVER} ${dimmed ? 'opacity-40' : ''}`}`}
                      onClick={manageable ? () => toggleExpand(person.id) : undefined}
                      title={!manageable
                        ? NOT_MANAGED_HERE_TITLE
                        : (isExpanded ? 'Click to collapse' : 'Click to expand settings')}
                    >
                      <td className={`${pad} font-medium ${isDeactivated ? DEACTIVATED_NAME_CLASS : ACTIVE_NAME_CLASS}`}>
                        <span className="flex items-center gap-1.5 min-w-0 max-w-full">
                          <span data-chevron className={`text-xs text-gray-400 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''} ${manageable ? '' : 'invisible'}`}>&#9654;</span>
                          {editing
                            ? <span className="min-w-0 flex-1">{textInput('display_name', 'text', 'Name')}</span>
                            : <span className="block min-w-0 max-w-[120px] md:max-w-none truncate" title={person.display_name}>{person.display_name}</span>}
                        </span>
                      </td>
                      {/* A PLAIN BADGE (owner 2026-10-19): the status `<select>` is
                          deleted; status changes only through the expanded row's
                          Actions menu (Pause / Reactivate / …) and Invite / Re-invite. */}
                      <td className={`${pad} truncate`}>
                        <span data-testid={`people-row-status-${person.id}`}
                          title={STATUS_TIPS[person.status]}
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap cursor-help ${badge.bg} ${badge.text}`}>
                          <UserStatusIndicator status={person.status} hideTitle />
                          {badge.label}
                        </span>
                      </td>
                      <td className={`${pad} hidden md:table-cell text-gray-500 dark:text-gray-400 ${editing ? '' : 'truncate'}`}
                        title={editing ? undefined : (person.email || undefined)}>
                        {/* A locked email stays a VALUE (the hint is under the row). */}
                        {editing && !emailLocked
                          ? textInput('email', 'email', 'Email', 'email@example.com')
                          : editing
                            ? <span className="block truncate px-1.5" data-testid="people-email-locked" title={EMAIL_LOCKED_HINT}>{person.email || '—'}</span>
                            : (person.email || '—')}
                      </td>
                      <td className={`${pad} hidden md:table-cell text-gray-500 dark:text-gray-400 ${editing ? '' : 'truncate'}`}>
                        {editing ? textInput('phone', 'tel', 'Phone', '+44 7700 900000') : (person.phone || '—')}
                      </td>
                      <td className={`${pad} hidden lg:table-cell text-gray-500 dark:text-gray-400 ${editing && activeSeat ? '' : 'truncate'}`}>
                        {editing && activeSeat && canChangeSeatRole(activeSeat.department_id) ? (
                          <div onClick={stopRowToggleOnControl}>
                            {roleSelect(activeSeat, person, {
                              className: ROW_EDIT_CONTROL,
                              testId: `people-row-role-${activeSeat.department_id}`,
                              ariaLabel: `${ACTIVE_ROLE_LABEL} in ${activeSeat.department_name}`,
                            })}
                          </div>
                        ) : (() => {
                          // Admin payloads carry `departmentRoles` (all-department
                          // list); every other payload keeps the sealed-box
                          // active-department role. See accessRightsInfo above.
                          const ar = accessRightsInfo(person);
                          if (!ar) return person.activeDepartmentRole?.role_display_name || '—';
                          return ar.text ? <span title={ar.title}>{ar.text}</span> : '—';
                        })()}
                      </td>
                      <td className={`${pad} hidden lg:table-cell text-gray-500 dark:text-gray-400 truncate`}>
                        {person.departments?.map(d => d.sub_department_name ? `${d.department_name} - ${d.sub_department_name}` : d.department_name).join(', ') || '—'}
                      </td>
                      {isGlobalAdmin && (
                        <td className={`${pad} hidden xl:table-cell text-gray-500 dark:text-gray-400 truncate`}>
                          {productionSummary(person.departments) || '—'}
                        </td>
                      )}
                    </tr>

                    {/* DESKTOP (md+): the house in-row panel — RowEditBar with
                        Actions ▾ on it, then the Departments table and the rest. */}
                    {!isMobile && (isExpanded || isClosing) && (
                      <InlineEditPanelRow
                        colSpan={isGlobalAdmin ? 7 : 6}
                        closing={isClosing}
                        fadeClass={fadeClass}
                        panelWidth={tableWrapWidth}
                        testId={`people-panel-${person.id}`}
                      >
                        <div ref={isExpanded ? expandedPanelRef : undefined} data-testid="people-panel-scroll"
                          className="scroll-mt-14 lg:scroll-mt-32 scroll-mb-24 lg:scroll-mb-4">
                          {expandedDetail && (
                            <RowEditBar
                              onSave={() => handleSaveEdit(person.id)}
                              onCancel={collapseUser}
                              saveTitle={SAVE_CHANGES_TIP}
                              cancelTitle={CANCEL_EDIT_TIP}
                              saving={saving}
                              dirty={isDirty}
                              testIdPrefix="people"
                              actions={actionsMenu(person, true)}
                            />
                          )}
                          <div className="p-4">
                            {!expandedDetail ? (
                              <div className="text-sm text-gray-400">Loading details...</div>
                            ) : (
                              <div className="grid gap-x-8 gap-y-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                                <div className="min-w-0">
                                  <h4 className={PANEL_HEADING_CLASS}>Departments</h4>
                                  {renderDepartments(person, expandedDetail, { stacked: false })}
                                </div>
                                <div className="min-w-0 space-y-3">
                                  {renderCrew(expandedDetail)}
                                  {emailLocked && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="people-email-locked-hint">{EMAIL_LOCKED_HINT}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </InlineEditPanelRow>
                    )}

                    {/* PHONE (< md): today's stacked panel and sticky Save bar
                        (plan constraint 5) — the row has no cells to edit in. */}
                    {isMobile && (isExpanded || isClosing) && (
                      <tr ref={isExpanded ? expandedPanelRef : undefined}
                        className="border-b border-gray-100 dark:border-gray-700 scroll-mt-14 lg:scroll-mt-32 scroll-mb-24 lg:scroll-mb-4"
                        onClick={(e) => e.stopPropagation()}>
                        <td colSpan={isGlobalAdmin ? 7 : 6} className="p-0">
                        <div className={`bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-600 p-4 space-y-4 transition-opacity duration-200 ease-out ${fadeClass}`}>
                          {!expandedDetail ? (
                            <div className="text-sm text-gray-400">Loading details...</div>
                          ) : (
                            <div className="space-y-4">
                              <div className="-mx-4 -mt-4 px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-gray-200 dark:border-gray-600"
                                data-section-title="" data-testid="people-panel-header">
                                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Person details</h3>
                                <div className="w-full">{actionsMenu(person, false)}</div>
                              </div>
                              <div className="space-y-3" data-testid="people-fields-stacked">
                                <div>
                                  <label className={FIELD_LABEL_CLASS}>Name</label>
                                  <input type="text" value={editForm.display_name ?? ''} onChange={setField('display_name')} className={FORM_INPUT_FIELD} />
                                </div>
                                <div>
                                  <label className={FIELD_LABEL_CLASS}>Email</label>
                                  <input type="email" value={editForm.email ?? ''} onChange={setField('email')}
                                    disabled={emailLocked}
                                    className={`${FORM_INPUT_FIELD} ${emailLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    placeholder="email@example.com" />
                                  {emailLocked && (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{EMAIL_LOCKED_HINT}</p>
                                  )}
                                </div>
                                <div>
                                  <label className={FIELD_LABEL_CLASS}>Phone</label>
                                  <input type="tel" value={editForm.phone ?? ''} onChange={setField('phone')} className={FORM_INPUT_FIELD} placeholder="+44 7700 900000" />
                                </div>
                              </div>
                              <div>
                                <h4 className={PANEL_HEADING_CLASS}>Departments</h4>
                                {renderDepartments(person, expandedDetail, { stacked: true })}
                              </div>
                              {renderCrew(expandedDetail)}
                              {/* 📱 POL2 A2 (2026-09-25), MEASURED in Chromium: the panel is
                                  ~928px tall on a phone, so Save/Cancel fell under the 65px
                                  bottom nav. This group is `contents` (so the save bar's
                                  containing block is the whole panel) and Save + Cancel ride
                                  a `sticky` bar above the nav.
                                  🪤 Needs the table wrapper's `md:overflow-x-auto` — a sticky
                                  element inside an overflow box resolves against THAT box. */}
                              <div className="contents md:flex md:flex-wrap md:items-center gap-x-4 gap-y-2 pt-2" data-testid="people-panel-footer">
                                <div data-testid="people-save-bar"
                                  className="sticky bottom-[65px] z-10 -mx-4 px-4 py-2 grid grid-cols-[1fr_auto] gap-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] md:contents">
                                  <button onClick={() => handleSaveEdit(person.id)} disabled={saving}
                                    data-testid="people-save"
                                    title={SAVE_CHANGES_TIP}
                                    className={`${BTN.confirm} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                  </button>
                                  <button onClick={() => collapseUser()}
                                    title={CANCEL_EDIT_TIP}
                                    className={`${BTN.secondary} min-h-[44px] md:min-h-0 inline-flex items-center justify-center`}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <Pagination
            totalItems={sortedPeople.length}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        </div>
      )}

      {/* A dirty row's three-way prompt (the house RowEditPrompt). */}
      <RowEditPrompt editor={rowPrompt} title="Unsaved changes" noun="This person" busy={saving} />

      {/* Impersonate confirmation — same wording as the window.confirm it
          replaced; default (blue) variant: it navigates, it saves nothing. The
          opener (the panel's Impersonate button) stays mounted while this is
          open, so ConfirmDialog's own focus return is enough. */}
      <ConfirmDialog
        open={!!impersonateTarget}
        title={impersonateTarget ? `Impersonate ${impersonateTarget.display_name}?` : ''}
        message="You will view the app as this user."
        confirmLabel="Impersonate"
        onCancel={() => setImpersonateTarget(null)}
        onConfirm={() => {
          const target = impersonateTarget;
          setImpersonateTarget(null);
          if (target) impersonate(target.id);
        }}
      />

      <ConfirmDialog
        open={!!deactivateTarget}
        variant="danger"
        initialFocus="cancel"
        title={deactivateTarget ? `Deactivate "${deactivateTarget.display_name}"?` : ''}
        message="They will no longer be able to log in."
        confirmLabel="Deactivate"
        cancelLabel="Keep active"
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={confirmDeactivate}
      />

      <ConfirmDialog
        open={!!statusChangeTarget}
        title={statusChangeTarget
          ? `Set "${statusChangeTarget.person.display_name}" to ${STATUS_BADGES[statusChangeTarget.to]?.label || statusChangeTarget.to}?`
          : ''}
        message={statusChangeTarget
          ? `Their status changes from ${STATUS_BADGES[statusChangeTarget.person.status]?.label || statusChangeTarget.person.status} to ${STATUS_BADGES[statusChangeTarget.to]?.label || statusChangeTarget.to}. No email is sent. The change is recorded against your name.`
          : ''}
        confirmLabel={statusChangeTarget ? `Set to ${STATUS_BADGES[statusChangeTarget.to]?.label || statusChangeTarget.to}` : 'Confirm'}
        initialFocus="cancel"
        onCancel={() => setStatusChangeTarget(null)}
        onConfirm={confirmStatusChange}
      />

      {/* Remove-from-production modal.
          - Buyer / production-admin context (activeProduction set): pass
            productionId so the modal jumps straight to confirm. The picker
            never renders for them — privacy boundary holds.
          - Site-admin context (no activeProduction): omit productionId so the
            modal renders its picker step listing the target user's productions
            from user.departments. */}
      {removeFromProductionTarget && (
        <RemoveUserFromProductionModal
          user={removeFromProductionTarget}
          productionId={activeProduction?.id}
          productionName={activeProduction?.production_name || activeProduction?.name}
          onClose={() => setRemoveFromProductionTarget(null)}
          onSuccess={() => {
            setSuccess(`"${removeFromProductionTarget.display_name}" removed from production`);
            setRemoveFromProductionTarget(null);
            collapseUser();
            fetchPeople(true);
          }}
        />
      )}

      {/* Global-offboard modal — nuclear, site admin only. */}
      {globalOffboardTarget && (
        <GlobalOffboardModal
          user={globalOffboardTarget}
          onClose={() => setGlobalOffboardTarget(null)}
          onSuccess={() => {
            setSuccess(`"${globalOffboardTarget.display_name}" deactivated globally`);
            setGlobalOffboardTarget(null);
            collapseUser();
            fetchPeople(true);
          }}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 ${MODAL_OVERLAY}`} onClick={() => setDeleteConfirm(null)}>
          <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md mx-4 ${MODAL_PANEL_MOBILE_CAP}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-2">
              Delete "{deleteConfirm.user.display_name}" permanently?
            </h3>

            {deleteConfirm.linkedData && (
              <div className="mb-4 space-y-1.5">
                {deleteConfirm.linkedData.severity === 'green' && (
                  <p className="text-sm text-green-600 dark:text-green-400">No linked data found. Safe to delete.</p>
                )}
                {deleteConfirm.linkedData.orders > 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {deleteConfirm.linkedData.orders} order(s) — will be unlinked (order data preserved)
                  </p>
                )}
                {deleteConfirm.linkedData.crew_members > 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    {deleteConfirm.linkedData.crew_members} crew member link(s) — will become unassigned
                  </p>
                )}
                {deleteConfirm.linkedData.approval_steps > 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {deleteConfirm.linkedData.approval_steps} approval step(s) — may affect active workflows
                  </p>
                )}
                {deleteConfirm.linkedData.workflow_steps > 0 && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {deleteConfirm.linkedData.workflow_steps} workflow template step(s) — may affect future approvals
                  </p>
                )}
                {deleteConfirm.linkedData.severity !== 'green' && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Consider deactivating instead if you want to preserve data.</p>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className={FIELD_LABEL_CLASS}>
                Type <strong>{deleteConfirm.user.display_name}</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirm.confirmName}
                onChange={(e) => setDeleteConfirm(prev => ({ ...prev, confirmName: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2 text-sm"
                placeholder={deleteConfirm.user.display_name}
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirm.confirmName !== deleteConfirm.user.display_name}
                className={`${BTN.danger} flex-1`}
              >
                Delete Permanently
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`${BTN.secondary} flex-1`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate confirmation dialog (owner ruling R1, 2026-10-21): names the
          status they return to — the server's restore_status, carried in
          `reactivateConfirm.to` — and keeps the limit of the action: PATCH
          /:id/status flips users.status only, so department seats wiped by a
          cascade offboard are NOT restored. The audit log's
          productions_at_offboard captures what to re-add by hand. */}
      {reactivateConfirm && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 ${MODAL_OVERLAY}`} onClick={() => setReactivateConfirm(null)}>
          <div data-testid="people-reactivate-dialog" className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md mx-4 ${MODAL_PANEL_MOBILE_CAP}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-2">
              Reactivate "{reactivateConfirm.person.display_name}"?
            </h3>
            <div className="mb-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p>
                They return to <strong>{STATUS_BADGES[reactivateConfirm.to]?.label || reactivateConfirm.to}</strong>,
                the status they had before they were paused.
              </p>
              <p>
                Department seats removed by an offboard are <strong>not</strong> restored. Re-add
                this person to those departments by hand.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The audit log records every production this user was on at the time of offboard
                — use that as your reference when re-assigning.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => {
                  const { person: target, to } = reactivateConfirm;
                  setReactivateConfirm(null);
                  await handleReactivate(target, to);
                }}
                className={`${BTN.confirm} flex-1`}
              >
                Reactivate
              </button>
              <button
                onClick={() => setReactivateConfirm(null)}
                className={`${BTN.secondary} flex-1`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
